import { getSubscriptionData } from '../helpers/dataDupe';
import { MIXPANEL_EVENTS } from './enums';
import { trackEvent } from './mixpanel';
import { getMixpanelPlatformProperties } from './mixpanelPlatform';
import { isSubscriptionTrialing } from './trialBilling';

const WEB_PLATFORM = 'web';
const UNKNOWN_PLAN_TYPE = 'unknown';
const WEB_EVENT_REGISTRY_PREFIX = 'vivian_web_analytics';
const WEB_PENDING_AUTH_KEY = `${WEB_EVENT_REGISTRY_PREFIX}:pending_auth`;
const WEB_PENDING_AUTH_MAX_AGE_MS = 15 * 60 * 1000;

const asString = value => (typeof value === 'string' ? value.trim() : '');

const getWebSessionStorage = () =>
  typeof window !== 'undefined' && window.sessionStorage ? window.sessionStorage : null;

const resolveBrowserCountry = () => {
  if (typeof navigator === 'undefined') {
    return undefined;
  }

  const localeCandidates = [navigator.language, ...(navigator.languages || [])]
    .map(value => asString(value))
    .filter(Boolean);

  for (const locale of localeCandidates) {
    const match = locale.match(/[-_](?<country>[a-z]{2})$/i);
    if (match?.groups?.country) {
      return match.groups.country.toUpperCase();
    }
  }

  return undefined;
};

const sanitizeProps = props =>
  Object.entries(props || {}).reduce((acc, [key, value]) => {
    if (value === undefined || value === null || value === '') {
      return acc;
    }

    acc[key] = value;
    return acc;
  }, {});

const readPendingWebAuth = () => {
  const storage = getWebSessionStorage();
  if (!storage) {
    return null;
  }

  const rawValue = storage.getItem(WEB_PENDING_AUTH_KEY);
  if (!rawValue) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(rawValue);
    const createdAt = Number(parsedValue?.createdAt);
    if (!createdAt || Date.now() - createdAt > WEB_PENDING_AUTH_MAX_AGE_MS) {
      storage.removeItem(WEB_PENDING_AUTH_KEY);
      return null;
    }

    return parsedValue;
  } catch (error) {
    storage.removeItem(WEB_PENDING_AUTH_KEY);
    return null;
  }
};

export const markPendingWebAuth = ({ signInMethod, mode = 'login' } = {}) => {
  const storage = getWebSessionStorage();
  const normalizedSignInMethod = asString(signInMethod).toLowerCase();
  const normalizedMode = asString(mode).toLowerCase();

  if (!storage || !normalizedSignInMethod || !normalizedMode) {
    return;
  }

  storage.setItem(
    WEB_PENDING_AUTH_KEY,
    JSON.stringify({
      signInMethod: normalizedSignInMethod,
      mode: normalizedMode,
      createdAt: Date.now(),
    })
  );
};

export const clearPendingWebAuth = () => {
  const storage = getWebSessionStorage();
  if (!storage) {
    return;
  }

  storage.removeItem(WEB_PENDING_AUTH_KEY);
};

export const consumePendingWebAuth = ({ mode } = {}) => {
  const storage = getWebSessionStorage();
  const pendingAuth = readPendingWebAuth();
  const normalizedMode = asString(mode).toLowerCase();

  if (!storage || !pendingAuth) {
    return null;
  }

  if (normalizedMode && pendingAuth.mode !== normalizedMode) {
    return null;
  }

  storage.removeItem(WEB_PENDING_AUTH_KEY);
  return pendingAuth;
};

const hasTrackedWebEvent = dedupeKey => {
  if (!dedupeKey || typeof window === 'undefined' || !window.sessionStorage) {
    return false;
  }

  return window.sessionStorage.getItem(`${WEB_EVENT_REGISTRY_PREFIX}:${dedupeKey}`) === '1';
};

const markWebEventTracked = dedupeKey => {
  if (!dedupeKey || typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }

  window.sessionStorage.setItem(`${WEB_EVENT_REGISTRY_PREFIX}:${dedupeKey}`, '1');
};

export const trackWebEventOnce = ({ eventName, properties, dedupeKey }) => {
  if (hasTrackedWebEvent(dedupeKey)) {
    return false;
  }

  trackEvent(eventName, properties);
  markWebEventTracked(dedupeKey);
  return true;
};

export const getWebUserId = currentUser =>
  asString(currentUser?.id?.uuid || (typeof currentUser?.id === 'string' ? currentUser?.id : ''));

export const normalizeWebCountry = value => {
  const country = asString(value);
  if (!country) {
    return undefined;
  }

  return /^[a-z]{2}$/i.test(country) ? country.toUpperCase() : country;
};

const resolvePlanTypeFromInterval = value => {
  const interval = asString(value).toLowerCase();

  if (!interval) {
    return undefined;
  }

  if (interval === 'month' || interval === 'monthly') {
    return 'monthly';
  }

  if (interval === 'year' || interval === 'annual' || interval === 'yearly') {
    return 'annual';
  }

  return undefined;
};

export const resolveWebPlanType = ({
  currentUser,
  planType,
  product,
  subscription,
} = {}) => {
  const currentSubscription = getSubscriptionData(currentUser);
  const explicitPlanType = resolvePlanTypeFromInterval(planType);
  if (explicitPlanType) {
    return explicitPlanType;
  }

  const candidates = [
    product?.recurring_interval,
    product?.interval,
    product?.price?.recurring?.interval,
    subscription?.items?.data?.[0]?.price?.recurring?.interval,
    subscription?.plan?.interval,
    subscription?.price?.recurring?.interval,
    subscription?.metadata?.plan_interval,
    currentSubscription?.items?.data?.[0]?.price?.recurring?.interval,
    currentSubscription?.plan?.interval,
    currentSubscription?.price?.recurring?.interval,
    currentSubscription?.metadata?.plan_interval,
  ];

  for (const candidate of candidates) {
    const resolvedPlanType = resolvePlanTypeFromInterval(candidate);
    if (resolvedPlanType) {
      return resolvedPlanType;
    }
  }

  return UNKNOWN_PLAN_TYPE;
};

export const resolveWebSubscriptionStatus = subscription => {
  if (!subscription) {
    return undefined;
  }

  if (isSubscriptionTrialing(subscription)) {
    return 'trialing';
  }

  const status = asString(subscription?.status).toLowerCase();
  if (!status) {
    return undefined;
  }

  if (status === 'active') {
    return 'active';
  }

  if (
    status === 'canceled' ||
    status === 'cancelled' ||
    status === 'incomplete_expired' ||
    status === 'expired' ||
    status === 'unpaid'
  ) {
    return 'churn';
  }

  return undefined;
};

const resolveStripePrice = ({ subscription, price }) => {
  if (typeof price === 'number' && Number.isFinite(price)) {
    return price;
  }

  const stripeUnitAmount = subscription?.items?.data?.[0]?.price?.unit_amount;
  return Number.isFinite(stripeUnitAmount) ? stripeUnitAmount / 100 : undefined;
};

const resolveStripeCurrency = ({ subscription, currency }) =>
  asString(currency) ||
  asString(subscription?.currency) ||
  asString(subscription?.items?.data?.[0]?.price?.currency) ||
  undefined;

export const buildWebAnalyticsProps = ({
  currentUser,
  userId,
  country,
  planType,
  product,
  subscription,
  properties = {},
} = {}) =>
  sanitizeProps({
    platform: WEB_PLATFORM,
    user_id: userId || getWebUserId(currentUser),
    country: normalizeWebCountry(
      country ||
        currentUser?.attributes?.profile?.protectedData?.country ||
        currentUser?.attributes?.protectedData?.country ||
        resolveBrowserCountry()
    ),
    plan_type: resolveWebPlanType({
      currentUser,
      planType,
      product,
      subscription,
    }),
    ...getMixpanelPlatformProperties(),
    ...properties,
  });

export const trackWebLoginCompleted = ({
  currentUser,
  signInMethod = 'email',
} = {}) =>
  trackEvent(
    MIXPANEL_EVENTS.LOGIN_COMPLETED,
    buildWebAnalyticsProps({
      currentUser,
      properties: {
        sign_in_method: signInMethod,
      },
    })
  );

export const trackWebSignupCompleted = ({
  currentUser,
  userId,
  country,
  planType,
} = {}) =>
  trackEvent(
    MIXPANEL_EVENTS.SIGNUP_COMPLETED,
    buildWebAnalyticsProps({
      currentUser,
      userId,
      country,
      planType,
    })
  );

export const trackWebCheckoutSuccess = ({
  currentUser,
  userId,
  country,
  planType,
  product,
  subscription,
  couponCode,
  price,
  currency,
} = {}) => {
  const commonProps = buildWebAnalyticsProps({
    currentUser,
    userId,
    country,
    planType,
    product,
    subscription,
  });
  const subscriptionStatus = resolveWebSubscriptionStatus(subscription);
  const normalizedCouponCode = asString(couponCode);
  const hasCoupon = Boolean(normalizedCouponCode);
  const subscriptionKey =
    asString(subscription?.id) ||
    `${commonProps.user_id || 'anonymous'}:${commonProps.plan_type || UNKNOWN_PLAN_TYPE}`;

  trackWebEventOnce({
    eventName: MIXPANEL_EVENTS.PURCHASE_COMPLETED,
    dedupeKey: `purchase_completed:${subscriptionKey}`,
    properties: sanitizeProps({
      ...commonProps,
      subscription_status: subscriptionStatus || 'active',
      price: resolveStripePrice({ subscription, price }),
      currency: resolveStripeCurrency({ subscription, currency }),
      has_coupon: hasCoupon,
      coupon_code: normalizedCouponCode || undefined,
      checkout_started: true,
    }),
  });

  if (subscriptionStatus === 'trialing') {
    trackWebEventOnce({
      eventName: MIXPANEL_EVENTS.START_FREE_TRIAL,
      dedupeKey: `start_free_trial:${subscriptionKey}`,
      properties: sanitizeProps({
        ...commonProps,
        subscription_provider: 'stripe_web',
        is_trial: true,
        subscription_status: 'trialing',
        has_coupon: hasCoupon,
        coupon_code: normalizedCouponCode || undefined,
      }),
    });
  }
};
